import { AiModelPrice } from '../service/ai/ai-prices.service';
import { AiPriceEvent } from '../service/ai/ai-watch.service';

// AI Price Watch (owner 2026-10-28): the pieces the board widget and the page's Compare Every Model share, so the
// list, the tape, the chart and the table read a price over a range the same way.

export type AiWatchField = 'input' | 'output' | 'cacheRead';
export type AiWatchRange = '1m' | '3m' | '6m' | 'ytd' | '1y';

export const AI_WATCH_FIELDS: { key: AiWatchField; label: string; hint: string }[] = [
  { key: 'output', label: 'Output', hint: 'What the model charges for every million tokens it writes back: the answer, the code, the reasoning' },
  { key: 'input', label: 'Input', hint: 'What you pay for every million tokens you send: the prompt, the files and the chat history' },
  { key: 'cacheRead', label: 'Cached Input', hint: 'Input the model reads back from its prompt cache, usually a small part of the input price' },
];

/** how far back the chart reads, and the compare table's columns */
export const AI_WATCH_RANGES: { key: AiWatchRange; label: string }[] = [
  { key: '1m', label: '1M' },
  { key: '3m', label: '3M' },
  { key: '6m', label: '6M' },
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y' },
];

export const AI_WATCH_DEFAULT_FIELD: AiWatchField = 'output';
export const AI_WATCH_DEFAULT_RANGE: AiWatchRange = '3m';

/** the vendors' own APIs: their models lead the lists */
export const AI_OWN_APIS = [ 'anthropic', 'openai', 'gemini', 'xai', 'mistral', 'deepseek' ];

/** one day of a price: where it opened, closed and went between */
export interface AiWatchDay {
  day: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

/** what reading a price needs besides the model: the price and the first day the watch has */
export interface AiWatchContext {
  field: AiWatchField;
  /** YYYY-MM-DD; no day before it is read */
  firstDay: string;
}

export function aiWatchYtdDays(): number {
  const now = new Date();
  return Math.floor( ( now.getTime() - Date.UTC( now.getUTCFullYear(), 0, 1 ) ) / 86400000 ) + 1;
}

/** the days a range covers, today included */
export function aiWatchRangeDays( range: AiWatchRange ): number {
  switch ( range ) {
    case '1m': return 30;
    case '6m': return 182;
    case 'ytd': return aiWatchYtdDays();
    case '1y': return 365;
    default: return 90;
  }
}

/** a day each from the first day the watch has (or the model's listing) to today, walking back from today's price */
export function aiWatchDays( m: AiModelPrice, events: AiPriceEvent[], span: number, ctx: AiWatchContext ): AiWatchDay[] {
  const now = m[ ctx.field ];
  if ( now === null ) { return []; }
  const today = new Date().toISOString().slice( 0, 10 );
  const first = ctx.firstDay || today;
  const keys: string[] = [];
  for ( const d = new Date( today + 'T00:00:00Z' ); keys.length < span; d.setUTCDate( d.getUTCDate() - 1 ) ) {
    const key = d.toISOString().slice( 0, 10 );
    if ( key < first ) { break; }
    keys.unshift( key );
  }
  const moves = events.filter( e => e.kind === 'changed' && e.field === ctx.field && e.before !== null && e.after !== null );
  const listed = events.find( e => e.kind === 'listed' );
  const days: AiWatchDay[] = [];
  let price = now;
  for ( let i = keys.length - 1; i >= 0; i-- ) {
    const day = keys[ i ];
    const close = price;
    const seen = [ close ];
    moves.filter( e => e.day === day ).forEach( e => { seen.push( e.after!, e.before! ); price = e.before!; } );
    days.unshift( { day, open: price, high: Math.max.apply( null, seen ), low: Math.min.apply( null, seen ), close } );
    if ( listed && listed.day === day ) { break; }
  }
  return days;
}

/** the change in percent from the first day's open to today's price, rounded to `decimals` */
export function aiWatchPct( now: number | null, days: AiWatchDay[], decimals = 0 ): number | null {
  const open = days.length ? days[ 0 ].open : null;
  const k = Math.pow( 10, decimals );
  return now !== null && open ? Math.round( ( now - open ) / open * 100 * k ) / k : null;
}

/** a smooth path through the points that never swings past them (monotone cubic), the part after the first point */
export function aiWatchCurve( pts: number[][] ): string {
  const n = pts.length;
  if ( n < 3 ) { return pts.slice( 1 ).map( pt => ` L${ pt.join( ',' ) }` ).join( '' ); }
  const dx: number[] = [], slope: number[] = [];
  for ( let i = 0; i < n - 1; i++ ) {
    dx.push( pts[ i + 1 ][ 0 ] - pts[ i ][ 0 ] );
    slope.push( ( pts[ i + 1 ][ 1 ] - pts[ i ][ 1 ] ) / dx[ i ] );
  }
  const t: number[] = [ slope[ 0 ] ];
  for ( let i = 1; i < n - 1; i++ ) {
    t.push( slope[ i - 1 ] * slope[ i ] <= 0 ? 0 : ( slope[ i - 1 ] + slope[ i ] ) / 2 );
  }
  t.push( slope[ n - 2 ] );
  for ( let i = 0; i < n - 1; i++ ) {
    if ( slope[ i ] === 0 ) { t[ i ] = t[ i + 1 ] = 0; continue; }
    const a = t[ i ] / slope[ i ], b = t[ i + 1 ] / slope[ i ], h = a * a + b * b;
    if ( h > 9 ) { const k = 3 / Math.sqrt( h ); t[ i ] = k * a * slope[ i ]; t[ i + 1 ] = k * b * slope[ i ]; }
  }
  const f = ( v: number ): string => v.toFixed( 1 );
  let out = '';
  for ( let i = 0; i < n - 1; i++ ) {
    const [ x0, y0 ] = pts[ i ], [ x1, y1 ] = pts[ i + 1 ], h = dx[ i ] / 3;
    out += ` C${ f( x0 + h ) },${ f( y0 + t[ i ] * h ) } ${ f( x1 - h ) },${ f( y1 - t[ i + 1 ] * h ) } ${ f( x1 ) },${ f( y1 ) }`;
  }
  return out;
}

/** the models of the vendors' own APIs first, then platform by platform, each by name */
export function aiWatchOrder( a: AiModelPrice, b: AiModelPrice ): number {
  const own = ( m: AiModelPrice ): number => ( AI_OWN_APIS.indexOf( m.platform ) + 1 || AI_OWN_APIS.length + 1 );
  return own( a ) - own( b ) || a.platform.localeCompare( b.platform ) || a.name.localeCompare( b.name );
}

/** the change in percent with its sign, "0%" for none */
export function aiWatchPctLabel( pct: number | null ): string {
  if ( pct === null ) { return ''; }
  return ( pct > 0 ? '+' : '' ) + pct + '%';
}
